import React, { createContext, SetStateAction, useContext, useRef, useState } from 'react';
import { usePost } from '../API';
import { TimedQueryResult } from '@aidkitorg/aidkit/lib/common/handler';
import * as v0 from '@aidkitorg/types/lib/survey';
import { logAdminPanelError } from '../utils/adminPanel.utils';
import { Dashboard } from '@aidkitorg/aidkit/lib/admin_panel/dashboards';

export type DashboardWithComponents = Dashboard & {
  components: Array<v0.DashboardComponent>
}

type StagedComponent = v0.HeroNumber
type StagedComponentWithResultType = TimedQueryResult & v0.HeroNumber

type DashboardsContextType = {
  dashboards: Array<Dashboard>
  getDashboards: () => void
  isDashboardsLoading: boolean,  

  fetchDashboardById: (id: string) => void,
  getDashboardById: (id: string) => DashboardWithComponents | void,

  // Dashboard Builder
  stagedComponents: Array<StagedComponent>,
  setStagedComponents: React.Dispatch<SetStateAction<Array<StagedComponent>>>,

  stagedComponentsWithResults: Array<StagedComponentWithResultType>,
  setStagedComponentsWithResults: React.Dispatch<SetStateAction<Array<StagedComponentWithResultType>>>,

  createDashboard: (title: string, description: string, components: Array<StagedComponent>) => void,
  getDashboardComponentTranslation: (component: v0.DashboardComponent) => Promise<TimedQueryResult | void | null>
}

type DashboardsById = {
  [dashboardId: string]: DashboardWithComponents
}

type DashboardsProviderProps = {
  children: React.ReactNode
}

const DashboardsContext = createContext<DashboardsContextType>({
  dashboards: [],
  getDashboards: async () => {},
  
  isDashboardsLoading: false,

  fetchDashboardById: async () => {},
  getDashboardById: () => {},

  stagedComponents: [],
  setStagedComponents: (_prev) => {},

  stagedComponentsWithResults: [],
  setStagedComponentsWithResults: (_prev) => {},

  createDashboard: (_title, _desc, _components) => {},
  getDashboardComponentTranslation: async (_component: v0.DashboardComponent) => {},
})

export const useDashboards = () => {
  const context = useContext(DashboardsContext)
  if (!context) {
    throw new Error("useDashboards must be used within a DashboardsProvider")
  }

  return context;
}

export const DashboardsProvider: React.FC<DashboardsProviderProps> = ({ children }) => {
  // TODO: update this array type
  const [dashboards, setDashboards] = useState<Array<any>>([])
  const [isDashboardsLoading, setIsDashboardsLoading] = useState(false)

  const [stagedComponents, setStagedComponents] = useState<Array<StagedComponent>>([])
  const [stagedComponentsWithResults, setStagedComponentsWithResults] = useState<Array<StagedComponentWithResultType>>([])

  const [dashboardsById, setDashboardsById] = useState<DashboardsById>({})

  const getAdminPanelDashboards = usePost("/api/admin_panel/dashboards")
  const fetchDashboardByIdPost = usePost("/api/admin_panel/dashboard")
  const translateDashboardComponent = usePost("/api/admin_panel/dashboards/component/translate")
  const createDashboardPost = usePost("/api/admin_panel/dashboards/create")

  const getDashboards = async () => {
    setIsDashboardsLoading(true)
    const result = await getAdminPanelDashboards({})
    setIsDashboardsLoading(false)

    if (result.success && result.data) {
      setDashboards(result.data.dashboards)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const fetchDashboardById = async (id: string) => {
    const result = await fetchDashboardByIdPost({dashboardId: id})

    if (result.success && result.data) {
      const dashboard = result.data
      setDashboardsById((prev) => (
        {...prev,
          [dashboard.id]: {
            id: dashboard.id,
            title: dashboard.title,
            description: dashboard.description,
            created_by: dashboard.created_by,
            updated_at: dashboard.updated_at,
            components: dashboard.components
          }
        }
      ))
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const getDashboardById = (id: string) => {
    return dashboardsById[id]
  }

  const getDashboardComponentTranslation = async (component: v0.DashboardComponent) => {
    const result = await translateDashboardComponent({component: component})

    if (result.success) {
      return result.data?.result
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const createDashboard = async (title: string, description: string, components: Array<StagedComponent>) => {
    const result = await createDashboardPost({
      title: title,
      description: description,
      components: components
    })

    if (result.success) {
      return;
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }

  const value = {
    dashboards,
    getDashboards, 
    isDashboardsLoading,

    fetchDashboardById,
    getDashboardById,

    stagedComponents,
    setStagedComponents,

    stagedComponentsWithResults,
    setStagedComponentsWithResults,

    createDashboard,
    getDashboardComponentTranslation: getDashboardComponentTranslation
  }
    
  return (
    <DashboardsContext.Provider value={value}>
      {children}
    </DashboardsContext.Provider>
  )
}